import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import { Divider, Spin, Typography } from 'antd'
import {
  GeckoDRA,
  GeckoSubDRA,
} from 'features/indicationTriage/customInfoTypes/rautReport'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useIndicationDetailCardExtension } from 'features/indicationTriage/IndicationDetailCardExtensionContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import isNil from 'lodash/isNil'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'
import useSWRImmutable from 'swr/immutable'

import WaterfallPlot from './trilatReportSignalCharts/WaterfallPlot'

export interface TriLatDetails {
  depth_conversion?: Record<string, { num: number; unit: string }>
  sensor_distance_conversion?: { num: number; unit: string }
  ascan_distance_conversion?: { num: number; unit: string }
  depths?: Record<string, [number, number]> // JSON keys are always strings
  ascan_index?: number
  sensor_index?: number
  sub_rating?: GeckoSubDRA
  dra_rating?: GeckoDRA
  gain_30?: number
  smoothing_30?: number
  gain_45?: number
  smoothing_45?: number
  probe_offset?: { num: number; unit: string }
  od_distance?: { num: number; unit: string }
  id_distance?: { num: number; unit: string }
  notes?: string
}

const { Text, Title: AntTitle } = Typography

const fileFetcher = async (url: string): Promise<number[][]> => {
  const res = fetch(url).then((resp) => resp.json())
  return res
}

const formatPath = (path: string | undefined) => {
  if (path) {
    return path.split('.')[0]
  }
  return ''
}

const useIndicationFiles = (indicationId: string | undefined) => {
  const { fileRefs } = useRolodexFileLS(
    useMemo(
      () =>
        indicationId
          ? {
              entry_id: indicationId,
              path: '/*',
            }
          : undefined,
      [indicationId]
    )
  )

  const { urls, isLoading: rolodexLoading } = useRolodexSignedURLs(fileRefs)

  const allFetch = React.useCallback(
    () =>
      Promise.all(
        urls.map(async (url, index) => ({
          data: await fileFetcher(url),
          filename: formatPath(fileRefs[index].path),
        }))
      ),
    [fileRefs, urls]
  )

  const { data, isLoading } = useSWRImmutable([urls], allFetch)

  const formattedData: Record<string, number[][]> = data
    ? data.reduce((acc: Record<string, number[][]>, current) => {
        acc[current.filename] = current.data
        return acc
      }, {})
    : {}
  return { data: formattedData, isLoading: isLoading && rolodexLoading }
}

type BScanInfo = {
  key: string
  angle: string
  data: number[][]
  displayName: string
}

// Helper to get DRA level display text
const getDRALevelText = (draRating?: GeckoDRA): string => {
  if (!draRating) return 'Unknown'
  // Extract the number from GR-DRA-X format
  const match = draRating.match(/GR-DRA-(\d+)/)
  return match ? `DRA Level ${match[1]}` : draRating
}

// B-Scan tile selector (stays in sidebar)
const BScanTileSelector: FC<{
  bscans: BScanInfo[]
  selectedBScan: string | null
  onSelectBScan: (key: string) => void
}> = ({ bscans, selectedBScan, onSelectBScan }) => {
  return (
    <div>
      <AntTitle level={5}>B-Scan Visualizations</AntTitle>
      <TileGrid>
        {bscans.map((bscan) => (
          <BScanTile
            key={bscan.key}
            $isSelected={selectedBScan === bscan.key}
            onClick={() => onSelectBScan(bscan.key)}>
            <TileAngle>{bscan.angle}°</TileAngle>
            <TileLabel>{bscan.displayName}</TileLabel>
          </BScanTile>
        ))}
      </TileGrid>
    </div>
  )
}

// Component for verdict and notes (stays in sidebar)
const VerdictAndNotesView: FC<{
  details?: TriLatDetails
  bscans: BScanInfo[]
  selectedBScan: string | null
  onSelectBScan: (key: string) => void
}> = ({ details, bscans, selectedBScan, onSelectBScan }) => {
  return (
    <ColumnWrapper>
      {bscans.length > 0 && (
        <>
          <BScanTileSelector
            bscans={bscans}
            selectedBScan={selectedBScan}
            onSelectBScan={onSelectBScan}
          />
          <Divider />
        </>
      )}
      <VerdictItem draRating={details?.dra_rating} />
      {details?.notes && (
        <>
          <Divider />
          <NotesSection notes={details.notes} />
        </>
      )}
    </ColumnWrapper>
  )
}

// Component for B-Scan display (portals to bottom panel)
const BScanBottomPanel: FC<{
  selectedBScan: BScanInfo | undefined
  details?: TriLatDetails
}> = ({ selectedBScan, details }) => {
  if (!selectedBScan) {
    return (
      <BottomPanelWrapper>
        <EmptyStateText>Select a B-Scan to view</EmptyStateText>
      </BottomPanelWrapper>
    )
  }

  const depthRange = details?.depths?.[selectedBScan.angle]
  const shapes = []

  if (depthRange && depthRange.length === 2) {
    const shape1 = {
      type: 'rect' as const,
      x0: 0,
      x1: selectedBScan.data ? selectedBScan.data.length : 0,
      y0: depthRange[0],
      y1: depthRange[1],
      line: {
        color: 'red',
        width: 3,
      },
      fillcolor: 'red',
      opacity: 0.2,
    }
    shapes.push(shape1)
  }

  return (
    <BottomPanelWrapper>
      <BScanHeader>
        <AntTitle level={4} style={{ margin: 0 }}>
          {selectedBScan.displayName} ({selectedBScan.angle}°)
        </AntTitle>
      </BScanHeader>
      <BScanContent>
        <PlotWrapper>
          <WaterfallPlot
            dataValues={selectedBScan.data}
            width={800}
            height={280}
            shapes={shapes}
          />
        </PlotWrapper>
        {depthRange && (
          <Text>
            <ColorSpan color='red'>Highlighted region </ColorSpan>
            shows the area of interest (depth range: {depthRange[0]} -{' '}
            {depthRange[1]})
          </Text>
        )}
      </BScanContent>
    </BottomPanelWrapper>
  )
}

// Verdict component that displays the DRA rating
const VerdictItem: FC<{ draRating?: GeckoDRA }> = ({ draRating }) => {
  return (
    <div>
      <AntTitle level={4}>
        Gecko Verdict:{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>
          {getDRALevelText(draRating)}
        </ColorSpan>
      </AntTitle>
      <Text strong>
        {draRating
          ? `Based on the analysis, this indication has been classified as ${getDRALevelText(draRating)}.`
          : 'Classification pending review.'}
      </Text>
    </div>
  )
}

// Notes section component
const NotesSection: FC<{ notes: string }> = ({ notes }) => {
  return (
    <div>
      <AntTitle level={4}>Notes</AntTitle>
      <Text>{notes}</Text>
    </div>
  )
}

export const Title = () => 'TriLat Plots'
export const Width = '24rem'
export const CardView: FC<IndicationDetailCardProps> = () => {
  const { indication: item } = useIndicationData()
  const extensionContext = useIndicationDetailCardExtension()
  const bottomPanelContainer = extensionContext?.bottomPanelContainer
  const [selectedBScanKey, setSelectedBScanKey] = useState<string | null>(null)

  const { data, isLoading } = useIndicationFiles(item?.id)

  // Extract TriLat details from the indication
  const trilatDetails = useMemo(() => {
    if (!item?.customInfo) return undefined
    return item.customInfo as TriLatDetails
  }, [item?.customInfo])

  // Process B-Scan data into structured format
  const bscans = useMemo<BScanInfo[]>(() => {
    if (isNil(data)) return []

    const angleMapping = ['0', '30', '45']

    return Object.entries(data)
      .map(([key, scanData]) => {
        const numericPart = key.replace('bscan_', '').replace(/[^0-9]/g, '')
        const bscanIndex = parseInt(numericPart)
        const angle = angleMapping[bscanIndex]

        return {
          key,
          angle,
          data: scanData,
          displayName: `B-Scan ${key.replace('bscan_', '')}`,
        }
      })
      .filter((bscan) => {
        // Filter out B-scans with empty or invalid data
        return (
          bscan.data &&
          bscan.data.length > 0 &&
          bscan.data.some((row) => row.length > 0)
        )
      })
  }, [data])

  // Auto-select first B-Scan when data loads
  React.useEffect(() => {
    if (bscans.length > 0 && !selectedBScanKey) {
      setSelectedBScanKey(bscans[0].key)
    }
  }, [bscans, selectedBScanKey])

  // Check if we have any bscan data
  const hasBScanData = bscans.length > 0

  // Get the selected B-Scan object
  const selectedBScan = useMemo(
    () => bscans.find((b) => b.key === selectedBScanKey),
    [bscans, selectedBScanKey]
  )

  // Request the auxiliary bottom panel when B-Scan data is available
  useEffect(() => {
    const requesterId = `trilat-bscan-${item?.id ?? 'unknown'}`
    const shouldRequest = !isLoading && hasBScanData

    if (shouldRequest && extensionContext) {
      extensionContext.requestAuxiliaryBottomPanel(requesterId)
      return () => extensionContext.releaseAuxiliaryBottomPanel(requesterId)
    }
  }, [item?.id, isLoading, hasBScanData, extensionContext])

  return (
    <>
      {/* Verdict, notes, and B-Scan tile selector stay in the sidebar card */}
      <div>
        <Spin
          indicator={<LoadingOutlined />}
          size='large'
          spinning={isLoading}
          style={{ minHeight: '50vh' }}>
          {!isLoading && hasBScanData ? (
            <VerdictAndNotesView
              details={trilatDetails}
              bscans={bscans}
              selectedBScan={selectedBScanKey}
              onSelectBScan={setSelectedBScanKey}
            />
          ) : !isLoading && !hasBScanData ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <Text type='secondary'>
                No B-Scan data available for this indication.
              </Text>
            </div>
          ) : null}
        </Spin>
      </div>

      {/* Selected B-Scan portals to the bottom panel */}
      {!isLoading &&
        hasBScanData &&
        bottomPanelContainer &&
        createPortal(
          <BScanBottomPanel
            selectedBScan={selectedBScan}
            details={trilatDetails}
          />,
          bottomPanelContainer
        )}
    </>
  )
}

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
`

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const BScanTile = styled.button<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0.5rem;
  border: 2px solid
    ${({ $isSelected }) =>
      $isSelected ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.15)'};
  border-radius: 0.5rem;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.background4 : theme.forecast.background2};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    background-color: ${({ theme }) => theme.forecast.background3};
  }

  &:active {
    transform: scale(0.98);
  }
`

const TileAngle = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.text1};
  line-height: 1;
`

const TileLabel = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.text3};
  margin-top: 0.25rem;
`

const BottomPanelWrapper = styled.div`
  width: 100%;
  height: 100%;
  max-height: 40vh;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow: hidden;
`

const BScanHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`

const BScanContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  overflow: hidden;
  min-height: 0;
`

const PlotWrapper = styled.div`
  width: 100%;
  max-width: 50rem;
  height: 100%;
  max-height: calc(40vh - 7.5rem);
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    max-width: 100%;
    max-height: 100%;
  }
`

const EmptyStateText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 1rem;
  color: ${({ theme }) => theme.forecast.text3};
`

const ColorSpan = styled.span<{ color: string }>`
  color: ${({ color }) => color};
`
